import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { 
  Users, 
  Calendar, 
  CheckCircle, 
  AlertCircle,
  TrendingUp,
  Award,
  Building,
  BarChart3,
  ChevronRight,
  ArrowUpRight,
  Filter,
  CalendarDays
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Progress } from "../components/ui/progress";
import { Button } from "../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  CartesianGrid,
  Legend,
  AreaChart,
  Area
} from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DATE_PERIODS = [
  { value: "all", label: "All Time" },
  { value: "this_month", label: "This Month" },
  { value: "last_month", label: "Last Month" },
  { value: "last_3_months", label: "Last 3 Months" },
  { value: "last_6_months", label: "Last 6 Months" },
  { value: "1_year", label: "Last Year" }
];

export default function AdminDashboard() {
  const [analytics, setAnalytics] = useState(null);
  const [trends, setTrends] = useState(null);
  const [filteredAnalytics, setFilteredAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState("all");

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (selectedPeriod !== "all") {
      fetchFilteredData();
    } else {
      setFilteredAnalytics(null);
    }
  }, [selectedPeriod]);

  const fetchData = async () => {
    try {
      const [analyticsRes, trendsRes] = await Promise.all([
        axios.get(`${API}/analytics/dashboard`),
        axios.get(`${API}/analytics/trends`)
      ]);
      setAnalytics(analyticsRes.data);
      setTrends(trendsRes.data);
    } catch (error) {
      console.error("Failed to fetch analytics:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchFilteredData = async () => {
    try {
      const response = await axios.get(`${API}/analytics/dashboard/filtered?period=${selectedPeriod}`);
      setFilteredAnalytics(response.data);
    } catch (error) {
      console.error("Failed to fetch filtered data:", error);
    }
  };

  // Use filtered data if available, otherwise use default analytics
  const displayData = filteredAnalytics || analytics;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const stats = [
    {
      label: "Active Users",
      value: displayData?.total_users || analytics?.total_users || 0,
      icon: Users,
      color: "text-primary",
      bg: "bg-primary/10",
      link: "/admin/users"
    },
    {
      label: selectedPeriod === "all" ? "Sessions This Month" : `Sessions (${DATE_PERIODS.find(p => p.value === selectedPeriod)?.label})`,
      value: displayData?.total_sessions || analytics?.sessions_this_month || 0,
      icon: Calendar,
      color: "text-accent",
      bg: "bg-accent/10",
      link: "/admin/reports"
    },
    {
      label: "Completed",
      value: displayData?.completed_sessions || analytics?.completed_sessions || 0,
      icon: CheckCircle,
      color: "text-[#059669]",
      bg: "bg-[#059669]/10",
      link: "/admin/compliance"
    },
    {
      label: "Compliance Rate",
      value: `${displayData?.compliance_rate || analytics?.compliance_rate || 0}%`,
      icon: AlertCircle,
      color: "text-[#D97706]",
      bg: "bg-[#D97706]/10",
      link: "/admin/compliance"
    }
  ];

  const completionData = [
    { name: "Completed", value: analytics?.completed_sessions || 0 },
    { name: "Pending", value: analytics?.missing_sessions || 0 }
  ];

  const COLORS = ["#0F5156", "#EAE7E2"];

  return (
    <div className="space-y-8 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-[#1A1A1A] tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Admin Dashboard
          </h1>
          <p className="text-[#5C5C5C] mt-1">
            {selectedPeriod === "all" 
              ? `Overview for ${analytics?.current_month || "this month"}`
              : `Filtered: ${DATE_PERIODS.find(p => p.value === selectedPeriod)?.label}`
            }
          </p>
        </div>
        
        {/* Date Filter */}
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 text-sm text-[#5C5C5C]">
            <CalendarDays className="w-4 h-4" />
            <span>Period:</span>
          </div>
          <Select value={selectedPeriod} onValueChange={setSelectedPeriod}>
            <SelectTrigger className="w-44">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {DATE_PERIODS.map(period => (
                <SelectItem key={period.value} value={period.value}>
                  {period.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat, index) => (
          <Link key={index} to={stat.link}>
            <Card 
              data-testid={`stat-card-${index}`}
              className="stats-card card-hover cursor-pointer group hover:shadow-xl hover:border-primary/20 transition-all"
            >
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-[#5C5C5C] mb-1 flex items-center gap-1">
                      {stat.label}
                      <ArrowUpRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </p>
                    <p className="text-3xl font-bold text-[#1A1A1A]" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      {stat.value}
                    </p>
                  </div>
                  <div className={`w-12 h-12 rounded-xl ${stat.bg} flex items-center justify-center group-hover:scale-110 transition-transform`}>
                    <stat.icon className={`w-6 h-6 ${stat.color}`} />
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>

      {/* Charts Row */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Average Score */}
        <Card className="stats-card" data-testid="avg-score-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-semibold flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-primary" />
              Average Score
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col items-center py-6">
              <div 
                className="score-circle"
                style={{ 
                  '--score': analytics?.average_score || 0,
                  background: `conic-gradient(#0F5156 ${(analytics?.average_score || 0) * 3.6}deg, #EAE7E2 0deg)`
                }}
              >
                <span className="score-value">{analytics?.average_score || 0}</span>
              </div>
              <p className="text-sm text-[#5C5C5C] mt-4">Organization Average</p>
            </div>
          </CardContent>
        </Card>

        {/* Completion Chart */}
        <Card className="stats-card" data-testid="completion-chart">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-semibold">Monthly Compliance</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[200px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={completionData}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={70}
                    paddingAngle={2}
                    dataKey="value"
                  >
                    {completionData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="flex justify-center gap-6 mt-2">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-primary" />
                <span className="text-sm text-[#5C5C5C]">Completed</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-secondary" />
                <span className="text-sm text-[#5C5C5C]">Pending</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Score Distribution */}
        <Card className="stats-card" data-testid="score-distribution">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-semibold">Score Ranges</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4 py-4">
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-[#5C5C5C]">Excellent (80-100)</span>
                  <span className="font-medium">{trends?.score_distribution?.excellent || 0}%</span>
                </div>
                <Progress value={trends?.score_distribution?.excellent || 0} className="h-2" />
              </div>
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-[#5C5C5C]">Good (60-79)</span>
                  <span className="font-medium">{trends?.score_distribution?.good || 0}%</span>
                </div>
                <Progress value={trends?.score_distribution?.good || 0} className="h-2" />
              </div>
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-[#5C5C5C]">Needs Work (0-59)</span>
                  <span className="font-medium">{trends?.score_distribution?.needs_work || 0}%</span>
                </div>
                <Progress value={trends?.score_distribution?.needs_work || 0} className="h-2" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Trends Section */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Monthly Trends Chart */}
        <Card className="stats-card" data-testid="monthly-trends">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-semibold flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-primary" />
              Monthly Session Trends
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[250px]">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={trends?.monthly_trends || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#EAE7E2" />
                  <XAxis dataKey="month" stroke="#9CA3AF" fontSize={12} />
                  <YAxis stroke="#9CA3AF" fontSize={12} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'white', 
                      border: '1px solid #EAE7E2',
                      borderRadius: '8px'
                    }}
                  />
                  <Legend />
                  <Area 
                    type="monotone" 
                    dataKey="sessions" 
                    name="Total Sessions"
                    stroke="#0F5156" 
                    fill="#0F5156" 
                    fillOpacity={0.3}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="completed" 
                    name="Completed"
                    stroke="#059669" 
                    fill="#059669" 
                    fillOpacity={0.3}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Score Trend Over Time */}
        <Card className="stats-card" data-testid="score-trends">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-semibold flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-accent" />
              Average Score Trend
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[250px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={trends?.monthly_trends || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#EAE7E2" />
                  <XAxis dataKey="month" stroke="#9CA3AF" fontSize={12} />
                  <YAxis domain={[0, 100]} stroke="#9CA3AF" fontSize={12} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'white', 
                      border: '1px solid #EAE7E2',
                      borderRadius: '8px'
                    }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="avg_score" 
                    name="Avg Score"
                    stroke="#E07A5F" 
                    strokeWidth={3}
                    dot={{ fill: '#E07A5F', r: 5 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Department Breakdown */}
      {trends?.department_breakdown?.length > 0 && (
        <Card className="stats-card" data-testid="department-breakdown">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-semibold flex items-center gap-2">
              <Building className="w-5 h-5 text-primary" />
              Department Performance
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Department</th>
                    <th>Users</th>
                    <th>Sessions</th>
                    <th>Avg Score</th>
                    <th>Performance</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {trends.department_breakdown.map((dept, index) => (
                    <tr key={index} data-testid={`dept-row-${index}`} className="hover:bg-slate-50 cursor-pointer group">
                      <td className="font-medium">
                        <Link to={`/admin/department/${encodeURIComponent(dept.department)}`} className="flex items-center gap-2 text-primary hover:underline">
                          {dept.department}
                          <ArrowUpRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                        </Link>
                      </td>
                      <td>{dept.users}</td>
                      <td>{dept.sessions}</td>
                      <td>
                        <span className={`badge ${
                          dept.avg_score >= 80 ? 'badge-success' :
                          dept.avg_score >= 60 ? 'badge-info' :
                          'badge-warning'
                        }`}>
                          {dept.avg_score}
                        </span>
                      </td>
                      <td className="w-32">
                        <Progress value={dept.avg_score} className="h-2" />
                      </td>
                      <td>
                        <Link to={`/admin/department/${encodeURIComponent(dept.department)}`}>
                          <ChevronRight className="w-5 h-5 text-slate-400 group-hover:text-primary transition-colors" />
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Top Performers */}
      <Card className="stats-card" data-testid="top-performers-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg font-semibold flex items-center gap-2">
            <Award className="w-5 h-5 text-accent" />
            Top Performers
          </CardTitle>
        </CardHeader>
        <CardContent>
          {analytics?.top_performers?.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Rank</th>
                    <th>Name</th>
                    <th>Department</th>
                    <th>Sessions</th>
                    <th>Avg Score</th>
                  </tr>
                </thead>
                <tbody>
                  {analytics.top_performers.map((performer, index) => (
                    <tr key={performer.id} data-testid={`performer-row-${index}`}>
                      <td>
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                          index === 0 ? 'bg-[#FFD700]/20 text-[#B8860B]' :
                          index === 1 ? 'bg-[#C0C0C0]/20 text-[#808080]' :
                          index === 2 ? 'bg-[#CD7F32]/20 text-[#8B4513]' :
                          'bg-secondary text-secondary-foreground'
                        }`}>
                          {index + 1}
                        </div>
                      </td>
                      <td className="font-medium">{performer.name}</td>
                      <td className="text-[#5C5C5C]">{performer.department}</td>
                      <td>{performer.sessions_completed}</td>
                      <td>
                        <span className={`badge ${
                          performer.average_score >= 80 ? 'badge-success' :
                          performer.average_score >= 60 ? 'badge-info' :
                          'badge-warning'
                        }`}>
                          {performer.average_score}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="empty-state py-12">
              <Award className="w-12 h-12 text-muted-foreground mb-4" />
              <p className="text-[#5C5C5C]">No performers data yet</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
